/**
 * Station services panel — the docked view. 8.2 shipped the venue; 8.3
 * lights up the MARKET: live local quotes (archetype supply/demand +
 * server drift), buy/sell straight through the server-authoritative
 * inventory. TRADE and BOARD stay honest placeholders for 8.4/8.5.
 */
import { ITEMS, MARKET_GOODS, type BoardListing } from "@hyperlight/shared";

export type MarketPrices = Record<string, { sell: number; buy: number }>;

export class StationPanel {
  private el: HTMLElement;
  private nameEl: HTMLElement;
  private subEl: HTMLElement;
  private servicesEl: HTMLElement;
  private marketEl: HTMLElement;
  private listEl: HTMLElement;
  private noteEl: HTMLElement;
  private tradeEl!: HTMLElement;
  private pilotsEl!: HTMLElement;
  private inviteEl!: HTMLElement;
  private boardEl!: HTMLElement;
  private blistEl!: HTMLElement;
  visible = false;
  private prices: MarketPrices | null = null;
  private pad = 0;

  constructor(
    private handlers: {
      onUndock: () => void;
      /** Ask the server for a fresh quote sheet. */
      onQuote: () => void;
      onTrade: (op: "buy" | "sell", id: string, n: number) => void;
      /** Live player stock + balance for rendering. */
      stockOf: (id: string) => number;
      credits: () => number;
      /** Other pilots docked at this station right now. */
      dockedPilots: () => { id: number; name: string }[];
      onInvite: (id: number) => void;
      onAccept: () => void;
      onDecline: () => void;
      /** Market board ops. */
      onBoard: (msg: {
        op: "browse" | "list" | "bid" | "buyout" | "cancel" | "claim";
        id?: string;
        item?: string;
        n?: number;
        price?: number;
        minBid?: number;
        durationS?: number;
      }) => void;
      held: () => string[];
    },
  ) {
    this.el = document.createElement("div");
    this.el.id = "station-panel";
    this.el.innerHTML = `
      <div class="sp-panel">
        <div class="sp-title"></div>
        <div class="sp-sub"></div>
        <div class="sp-services">
          <button class="sp-svc" data-svc="market">MARKET<span>buy & sell goods</span></button>
          <button class="sp-svc" data-svc="trade">TRADE<span>with docked pilots</span></button>
          <button class="sp-svc" data-svc="board">BOARD<span>listings & auctions</span></button>
        </div>
        <div class="sp-market" style="display:none">
          <div class="sp-note"></div>
          <div class="sp-list"></div>
          <button class="sp-back">← SERVICES</button>
        </div>
        <div class="sp-tradeview" style="display:none">
          <div class="sp-note sp-tnote"></div>
          <div class="sp-pilots"></div>
          <button class="sp-back sp-tback">← SERVICES</button>
        </div>
        <div class="sp-board" style="display:none">
          <div class="sp-note sp-bnote"></div>
          <div class="sp-sell">
            <select class="sp-sell-item"></select>
            <input class="sp-sell-n" type="number" min="1" value="1" title="quantity" />
            <input class="sp-sell-price" type="number" min="1" placeholder="price" />
            <select class="sp-sell-mode">
              <option value="listing">FIXED</option>
              <option value="auction">AUCTION</option>
            </select>
            <select class="sp-sell-dur">
              <option value="600">10 MIN</option>
              <option value="3600">1 HR</option>
              <option value="86400">24 HR</option>
            </select>
            <button class="sp-sell-go">LIST</button>
          </div>
          <div class="sp-blist"></div>
          <button class="sp-back sp-bback">← SERVICES</button>
        </div>
        <div class="sp-invite" style="display:none">
          <div class="sp-inv-text"></div>
          <div class="sp-inv-actions">
            <button class="sp-accept">ACCEPT</button>
            <button class="sp-decline">DECLINE</button>
          </div>
        </div>
        <button class="sp-undock">UNDOCK</button>
      </div>`;
    document.body.appendChild(this.el);
    this.nameEl = this.el.querySelector(".sp-title")!;
    this.subEl = this.el.querySelector(".sp-sub")!;
    this.servicesEl = this.el.querySelector(".sp-services")!;
    this.marketEl = this.el.querySelector(".sp-market")!;
    this.listEl = this.el.querySelector(".sp-list")!;
    this.noteEl = this.el.querySelector(".sp-note")!;
    this.el.querySelector(".sp-undock")!.addEventListener("click", () => this.handlers.onUndock());
    this.el.querySelector('[data-svc="market"]')!.addEventListener("click", () => {
      this.servicesEl.style.display = "none";
      this.marketEl.style.display = "";
      this.noteEl.textContent = "FETCHING QUOTES…";
      this.handlers.onQuote();
    });
    this.el.querySelector(".sp-back")!.addEventListener("click", () => {
      this.marketEl.style.display = "none";
      this.servicesEl.style.display = "";
      this.noteEl.textContent = "";
    });
    this.tradeEl = this.el.querySelector(".sp-tradeview")!;
    this.pilotsEl = this.el.querySelector(".sp-pilots")!;
    this.inviteEl = this.el.querySelector(".sp-invite")!;
    this.el.querySelector('[data-svc="trade"]')!.addEventListener("click", () => {
      this.servicesEl.style.display = "none";
      this.tradeEl.style.display = "";
      this.renderPilots();
    });
    this.el.querySelector(".sp-tback")!.addEventListener("click", () => {
      this.tradeEl.style.display = "none";
      this.servicesEl.style.display = "";
    });
    this.pilotsEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".sp-invitebtn") as HTMLElement | null;
      if (btn) this.handlers.onInvite(Number(btn.dataset.id));
    });
    this.el.querySelector(".sp-accept")!.addEventListener("click", () => {
      this.inviteEl.style.display = "none";
      this.handlers.onAccept();
    });
    this.el.querySelector(".sp-decline")!.addEventListener("click", () => {
      this.inviteEl.style.display = "none";
      this.handlers.onDecline();
    });
    this.boardEl = this.el.querySelector(".sp-board")!;
    this.blistEl = this.el.querySelector(".sp-blist")!;
    this.el.querySelector('[data-svc="board"]')!.addEventListener("click", () => {
      this.servicesEl.style.display = "none";
      this.boardEl.style.display = "";
      this.el.querySelector(".sp-bnote")!.textContent = "FETCHING THE BOARD…";
      this.renderSellForm();
      this.handlers.onBoard({ op: "browse" });
    });
    this.el.querySelector(".sp-bback")!.addEventListener("click", () => {
      this.boardEl.style.display = "none";
      this.servicesEl.style.display = "";
    });
    this.el.querySelector(".sp-sell-go")!.addEventListener("click", () => {
      const item = (this.el.querySelector(".sp-sell-item") as HTMLSelectElement).value;
      const n = Math.max(1, Math.floor(Number((this.el.querySelector(".sp-sell-n") as HTMLInputElement).value) || 1));
      const price = Math.max(1, Math.floor(Number((this.el.querySelector(".sp-sell-price") as HTMLInputElement).value) || 0));
      const mode = (this.el.querySelector(".sp-sell-mode") as HTMLSelectElement).value;
      const durationS = Number((this.el.querySelector(".sp-sell-dur") as HTMLSelectElement).value);
      if (!item || price < 1) return;
      this.handlers.onBoard(
        mode === "auction"
          ? { op: "list", item, n, minBid: price, durationS }
          : { op: "list", item, n, price, durationS },
      );
    });
    this.blistEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".sp-bact") as HTMLElement | null;
      if (!btn) return;
      this.handlers.onBoard({
        op: btn.dataset.op as "bid" | "buyout" | "cancel" | "claim",
        id: btn.dataset.id,
      });
    });
    // One delegated handler for every buy/sell button.
    this.listEl.addEventListener("click", (e) => {
      const btn = (e.target as HTMLElement).closest(".sp-trade") as HTMLElement | null;
      if (!btn) return;
      const op = btn.dataset.op as "buy" | "sell";
      const id = btn.dataset.id!;
      const n = (e as MouseEvent).shiftKey ? 10 : 1;
      this.handlers.onTrade(op, id, n);
    });
  }

  open(name: string, pad: number, _credits: number): void {
    this.pad = pad;
    this.nameEl.textContent = name.toUpperCase();
    this.refreshHeader();
    this.servicesEl.style.display = "";
    this.marketEl.style.display = "none";
    this.tradeEl.style.display = "none";
    this.boardEl.style.display = "none";
    this.inviteEl.style.display = "none";
    this.el.style.display = "flex";
    this.visible = true;
  }

  /** Server quote sheet landed (or a trade settled). */
  setPrices(prices: MarketPrices, err?: string): void {
    this.prices = prices;
    this.noteEl.textContent = err ?? "shift-click trades ×10 · prices drift";
    this.noteEl.classList.toggle("sp-err", !!err);
    this.renderList();
    this.refreshHeader();
  }

  private renderSellForm(): void {
    const sel = this.el.querySelector(".sp-sell-item") as HTMLSelectElement;
    sel.innerHTML = this.handlers
      .held()
      .map((id) => `<option value="${id}">${(ITEMS[id]?.name ?? id).toUpperCase()}</option>`)
      .join("");
  }

  /** Board entries from the server. */
  setBoard(listings: BoardListing[], err?: string): void {
    const note = this.el.querySelector(".sp-bnote")!;
    note.textContent = err ?? "fixed listings buy instantly · auctions settle at expiry";
    note.classList.toggle("sp-err", !!err);
    const now = Date.now();
    const fmtT = (ms: number): string => {
      const s = Math.max(0, Math.round((ms - now) / 1000));
      return s >= 3600 ? `${Math.floor(s / 3600)}h` : s >= 60 ? `${Math.floor(s / 60)}m` : `${s}s`;
    };
    const rows: string[] = [];
    for (const l of listings) {
      const name = (ITEMS[l.item]?.name ?? l.item).toUpperCase();
      if (l.claimable) {
        rows.push(`<div class="sp-row"><div class="sp-good">${name} × ${l.n}<span>ready to claim</span></div>
          <button class="sp-trade sp-bact" data-op="claim" data-id="${l.id}">CLAIM</button></div>`);
        continue;
      }
      const tag = l.kind === "auction"
        ? `bid ${l.bid ?? "—"}${l.bidderName ? ` (${l.bidderName})` : ""} · ${fmtT(l.expiresAt)}`
        : `${l.sellerName} · ${fmtT(l.expiresAt)}`;
      const act = l.mine
        ? `<button class="sp-trade sp-bact" data-op="cancel" data-id="${l.id}" ${l.bid ? "disabled" : ""}>CANCEL</button>`
        : l.kind === "auction"
          ? `<button class="sp-trade sp-bact" data-op="bid" data-id="${l.id}">BID ${l.minBid}</button>`
          : `<button class="sp-trade sp-bact" data-op="buyout" data-id="${l.id}">BUY ${l.price}</button>`;
      rows.push(`<div class="sp-row"><div class="sp-good">${name} × ${l.n}<span>${tag}</span></div>${act}</div>`);
    }
    this.blistEl.innerHTML = rows.join("") || `<div class="sp-row"><div class="sp-good">NOTHING LISTED<span>be the first</span></div></div>`;
  }

  /** Trade-view pilot list. */
  renderPilots(): void {
    const pilots = this.handlers.dockedPilots();
    this.el.querySelector(".sp-tnote")!.textContent = pilots.length
      ? "invite a docked pilot to trade"
      : "no other pilots docked here";
    this.pilotsEl.innerHTML = pilots
      .map(
        (p) =>
          `<div class="sp-row"><div class="sp-good">${p.name.toUpperCase()}</div>
           <button class="sp-trade sp-invitebtn" data-id="${p.id}">INVITE</button></div>`,
      )
      .join("");
  }

  /** Incoming trade invite prompt. */
  showInvite(name: string): void {
    this.el.querySelector(".sp-inv-text")!.textContent =
      `${name.toUpperCase()} WANTS TO TRADE`;
    this.inviteEl.style.display = "";
  }

  hideInvite(): void {
    this.inviteEl.style.display = "none";
  }

  /** Inventory/credits changed — keep stock and balance live. */
  refresh(): void {
    if (!this.visible) return;
    this.refreshHeader();
    if (this.marketEl.style.display !== "none" && this.prices) this.renderList();
  }

  private refreshHeader(): void {
    this.subEl.textContent = `PAD ${this.pad + 1} — DOCKED · ${this.handlers.credits()} CR`;
  }

  private renderList(): void {
    if (!this.prices) return;
    const rows: string[] = [];
    for (const id of Object.keys(MARKET_GOODS)) {
      const q = this.prices[id];
      if (!q) continue;
      const def = ITEMS[id];
      const stock = this.handlers.stockOf(id);
      rows.push(`
        <div class="sp-row">
          <div class="sp-good">${(def?.name ?? id).toUpperCase()}<span>× ${stock}</span></div>
          <button class="sp-trade" data-op="buy" data-id="${id}">BUY ${q.sell}</button>
          <button class="sp-trade" data-op="sell" data-id="${id}" ${stock === 0 ? "disabled" : ""}>SELL ${q.buy}</button>
        </div>`);
    }
    this.listEl.innerHTML = rows.join("");
  }

  close(): void {
    this.el.style.display = "none";
    this.visible = false;
    this.prices = null;
  }
}
